import { html, LitElement } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { classMap } from 'lit/directives/class-map.js'
import { DirectionController } from '../common/controllers/DirectionController.js'
import { SlotController } from '../common/controllers/SlotController.js'
import componentStyle from '../common/styles/Component.css'
import style from './ButtonGroup.css'

/**
 * Button groups are designed to bring together button controls that are of a similar nature. For example text formatting controls.
 *
 * @status ready
 * @category action
 * @slot - The button group content
 *
 * @cssprop [--n-button-group-border-radius=var(--n-border-radius-s)] - Controls the rounded corners of the button group, using [border radius tokens](/tokens/#border-radius). Only relevant for the default variant.
 * @cssprop [--n-button-group-box-shadow=var(--n-box-shadow)] - Controls the surrounding shadow, using [box shadow tokens](/tokens/#box-shadow). Only relevant for the default variant.
 * @cssprop [--n-button-group-gap=calc(var(--n-space-s) + var(--n-space-xs))] - Controls the gap between buttons, using [space tokens](/tokens/#space). Only relevant for the spaced variant. Defaults to `var(--n-space-s)` when all slotted controls are `size="s"`.
 */
@customElement('nord-button-group')
export default class ButtonGroup extends LitElement {
  static styles = [componentStyle, style]

  private defaultSlot = new SlotController(this)
  private dirController = new DirectionController(this)

  /**
   * The style variant of the button group.
   */
  @property({ reflect: true }) variant: 'default' | 'spaced' = 'default'

  /**
   * The direction of the button group.
   */
  @property({ reflect: true }) direction: 'vertical' | 'horizontal' = 'horizontal'

  /**
   * Defines whether the buttons are forced in a single line
   * or can be flowed into multiple lines (only applied when variant is set to `spaced`).
   */
  @property({ reflect: true, type: Boolean }) wrap = false

  /**
   * The appropriate role for the containing element.
   */

  @property({ reflect: true }) role: string = 'group'

  render() {
    const hasSmallButtons = this.defaultSlot.assigned.every((node) => {
      if (node.localName === 'nord-button' || node.localName === 'nord-select') {
        return node.getAttribute('size') === 's'
      }

      return true
    })

    const hasMultipleButtons = this.defaultSlot.assigned.length > 1

    return html`
      <div
        class=${classMap({
          'n-button-group': true,
          'n-rtl': this.dirController.isRTL,
          'n-small-gap': hasSmallButtons && this.variant === 'spaced',
          'n-multiple-buttons': hasMultipleButtons,
        })}
      >
        <slot></slot>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-button-group': ButtonGroup
  }
}
